import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './EmployeeDetails.css';
import Table from '../../../Table/Table';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../utils/axiosInstance';

const EmployeeDetails = ({ project }) => {
  const { t } = useTranslation();
  const [employeeWorkData, setEmployeeWorkData] = useState([]);
  const [projectAssignIds, setProjectAssignIds] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!project || !project.id) return; // Ensure project and project.id are defined before fetching data

    const fetchData = async () => {
      try {
        // Fetch empProjRelation to get assign_ids associated with the project_id
        const assignmentsResponse = await axiosInstance.get('/admin/empProjRelation');
        const assignments = assignmentsResponse.data;

        // Filter assign_ids based on the project_id
        const projectAssignIds = assignments
          .filter(assignment => assignment.project_id === project.id)
          .map(assignment => assignment.assign_id);

        setProjectAssignIds(projectAssignIds);

        // Now fetch ProjWork data using the filtered assign_ids
        const workResponse = await axiosInstance.get('/admin/empProjWork');
        const data = workResponse.data;

        if (!data) return; // Check if data is null or undefined

        // Filter ProjWork data based on projectAssignIds
        const filteredLogs = data.filter(log => projectAssignIds.includes(log.assign_id));

        const processedData = project.employees.map(employee => {
          const employeeLogs = filteredLogs.filter(log => log.p_name === employee.name);

          const todayLogs = employeeLogs.filter(log => new Date(log.work_date).toDateString() === new Date().toDateString());
          const totalDuration = calculateTotalDuration(employeeLogs);
          const todayDuration = calculateTotalDuration(todayLogs);
          const totalOvertime = calculateOvertimeDuration(employeeLogs);

          return [
            employee.id ? employee.id.toString() : 'N/A',
            // employee.sap_id ? employee.sap_id : 'N/A',
            employee.name || 'N/A',
            formatDuration(todayDuration),
            formatDuration(totalOvertime),
            formatDuration(totalDuration)
          ];
        });

        setEmployeeWorkData(processedData);
      } catch (error) {
        console.error('Error fetching data:', error.message);
      }
    };

    fetchData();
  }, [project]);

  const calculateTotalDuration = (logs) => {
    return logs.reduce((total, log) => {
      if (!log || !log.work_duration) return total; // Ensure log and log.work_duration are not null or undefined
      const [hours, minutes, seconds] = log.work_duration.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return total; // Ensure parsed values are numbers
      return total + (hours * 3600) + (minutes * 60) + seconds;
    }, 0);
  };

  const calculateOvertimeDuration = (logs) => {
    return logs.reduce((total, log) => {
      if (log.overtime === "Overtime" && log.work_duration) {
        const [hours, minutes, seconds] = log.work_duration.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return total; // Ensure parsed values are numbers
        return total + (hours * 3600) + (minutes * 60) + seconds;
      }
      return total;
    }, 0);
  };

  const formatDuration = (seconds) => {
    if (isNaN(seconds)) return '00:00'; // Handle NaN case
    const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const headers = [t("projectDetails_EmployeeID"), t("attendance_name"), t("projectDetails_todayWork"), t("projectDetails_totalOvertime"), t("projectDetails_totalWorking")];

  const handleEmployeeClick = (employee) => {
    const employeeId = employee.id.toString();
    navigate(`/employee/${employee.name}`, {
      state: {
        employeeId,
        employeeName: employee.name,
        project,
        projectAssignIds,
      }
    });
  };

  return (
    <div className="EmployeeDetails-body">
      <div className="EmployeeDetails-header">
        <span className="EmployeeDetails-headerTitle">{t("projectDetails_empWorkTimeTitle")}</span>
        <span className='CurrentlyWorking-active'>
          {t("projectDetails_empWorkTimeformat")}
        </span>
      </div>
      <div className="EmployeeDetails-table-body">
        <Table
          headers={headers}
          data={employeeWorkData}
          onRowClick={(row) => handleEmployeeClick({ id: row[0], name: row[1] })}
        />
      </div>
    </div>
  );
};

export default EmployeeDetails;
